/**
 * Types for the admin Card Manager surface.
 * Mirrors the backend interfaces in server/services/card-sync.ts and
 * server/routes/admin/cards.ts.
 */

export interface AdminCardRow {
  id: string
  set_id: string
  number: string
  name: string | null
  rarity: string | null
  art_type: string | null
  sub_art_type: string | null
  image_url: string | null
  type: string | null
  cost: number | null
  power: number | null
  counter: number | null
  life: number | null
  attributes: string | null
  is_manga: number | null
  manually_edited_fields: string | null   // raw JSON array
  last_ct_sync_at: string | null
  last_ct_blueprint_id: number | null
  blueprint_count: number
  cardmarket_url?: string | null
  // Phase 2 additions (migration 034) — present on every modern card row.
  foil_variant?: 'regular' | 'foil' | 'gold_foil' | null
  is_errata?: number | null
  errata_reason?: string | null
  errata_of?: string | null
  errata_published_at?: string | null
  promo_subtype?: string | null
}

export type AdminCardSortKey =
  | 'id'
  | 'name'
  | 'set_id'
  | 'rarity'
  | 'last_ct_sync_at'
  | 'blueprint_count'

export interface AdminCardListParams {
  q?: string
  set_id?: string
  rarity?: string
  has_image?: boolean
  has_cardmarket_url?: boolean
  has_blueprint?: boolean
  duplicates_only?: boolean
  limit?: number
  offset?: number
  sort?: AdminCardSortKey
  dir?: 'asc' | 'desc'
}

export interface AdminCardListResponse {
  cards: AdminCardRow[]
  total: number
}

export interface AdminBlueprintMapping {
  id: number
  blueprint_id: number
  card_id: string
  expansion_id: number | null
  expansion_name: string | null
  blueprint_name: string | null
  blueprint_number: string | null
  collector_number: string | null
  blueprint_version: string | null
  image_url: string | null
  onepiece_rarity: string | null
  language: string | null
  mapping_method: string
  confidence: number
  verified_at: string | null
}

export interface AdminAuditEntry {
  id: number
  user_id: number | null
  user_name?: string | null
  action: string
  before_json: string | null
  after_json: string | null
  metadata_json: string | null
  created_at: string
  undone_at?: string | null
  undone_by_audit_id?: number | null
  is_undone?: boolean
  before?: any
  after?: any
  metadata?: any
}

export interface UnmappedBlueprintRow {
  id: number
  blueprint_id: number
  expansion_id: number | null
  expansion_name: string | null
  blueprint_name: string
  blueprint_number: string | null
  collector_number: string | null
  blueprint_version: string | null
  image_url: string | null
  onepiece_rarity: string | null
  language: string | null
  mapping_method: string
  confidence: number
  suggestions: Array<{
    cardId: string
    confidence: number
    method: string
    card: {
      id: string
      name: string | null
      set_id: string
      number: string
      rarity: string | null
      image_url: string | null
    } | null
  }>
}

export interface BlueprintCandidate extends AdminBlueprintMapping {
  derived_name?: string | null
  derived_tier?: string | null
  derived_art_type?: string | null
}

export interface BlueprintCandidatesResponse {
  card: AdminCardRow & Record<string, any>
  candidates: BlueprintCandidate[]
}

export interface BulkOpResult<T = any> {
  ok: boolean
  batchId: string
  results: T[]
  created?: string[]
}

export interface CardUrlValidation {
  card_id: string
  url: string
  status_code: number | null
  checked_at: string
  error: string | null
  card_name: string | null
  set_id: string | null
}

export interface AdminCardDetail {
  card: AdminCardRow & Record<string, any>
  mappings: AdminBlueprintMapping[]
  audit: AdminAuditEntry[]
}

export interface MergePreflightImpactRow {
  table: string
  rows_from: number
  conflicts: number
  notes?: string
}

export interface MergePreflightSummary {
  from: AdminCardRow & Record<string, any>
  to: AdminCardRow & Record<string, any>
  impact: MergePreflightImpactRow[]
}

export interface SyncOptions {
  dryRun: boolean
  batchSize?: number
  setIds?: string[]
  cardIds?: string[]
  createMissing?: boolean
  resumeBatchId?: string
  canonicalStrategy?: 'lowest_blueprint_id'
}

export interface SyncFieldChange {
  field: string
  before: any
  after: any
  skipped?: 'manually_edited'
}

export interface SyncCardChange {
  card_id: string
  blueprint_id: number
  fields: SyncFieldChange[]
}

export interface SyncCardSkip {
  card_id: string
  reason: string
}

export interface SyncCardConflict {
  card_id: string
  blueprint_ids: number[]
  chosen_blueprint_id: number
  reason: string
}

export interface SyncCardError {
  card_id?: string
  blueprint_id?: number
  message: string
}

export interface SyncCardCreated {
  card_id: string
  blueprint_id: number
  set_id: string
  number: string
  name: string
}

export interface CardSyncReport {
  batchId: string
  startedAt: string
  finishedAt: string
  dryRun: boolean
  options: SyncOptions
  totals: {
    added: number
    updated: number
    skipped: number
    conflicts: number
    errors: number
  }
  added: SyncCardCreated[]
  updated: SyncCardChange[]
  skipped: SyncCardSkip[]
  conflicts: SyncCardConflict[]
  errors: SyncCardError[]
}

/**
 * Per-set health stats row used by the Sets-tab drill-down list.
 * Mirrors the response of GET /api/admin/cards/sets/stats.
 */
export interface AdminSetStatsRow {
  id: string
  name: string
  code: string | null
  release_date: string | null
  total_cards: number | null
  actual_cards: number
  with_image: number
  with_ct_mapping: number
  with_cm_url: number
  with_recent_sync: number
  last_ct_sync_at: string | null
  conflict_count: number
  manually_edited_count: number
  // TODO Phase 4: extend GET /sets/stats to include set_type + rotation_date so
  // the Sets-tab list can show & sort by them. For now EditSetModal fetches
  // these on demand via setsApi.getById.
  set_type?: string | null
  rotation_date?: string | null
  // Phase 4b additions — optional because GET /sets/stats may not surface them
  // yet. The frontend falls back to fetching the full set via setsApi.getById
  // when these are missing.
  seed_source?: SetSeedSource | null
  last_synced_at?: string | null
}

export interface AdminSetsStatsResponse {
  sets: AdminSetStatsRow[]
  stale_days: number
}

// ---------------------------------------------------------------------------
// Phase 4 (per-set sync) types — match server/routes/admin/cards.per-set-sync.ts
// envelopes. Backend tests live in cards.per-set-sync.test.ts.
// ---------------------------------------------------------------------------

/** A `seed_source` value — drives the per-row health badge in the Sets tab. */
export type SetSeedSource = 'json' | 'cardtrader' | 'manual'

/** Per-set parity recommendation returned by GET /parity/:setId. */
export type ParityRecommendation = 'cardtrader' | 'json' | 'review'

/** GET /sync/by-set/:setId/diff response envelope. */
export interface PerSetDiffSetSummary {
  id: string
  name: string
  seed_source: SetSeedSource | null
  last_synced_at: string | null
  total_cards: number | null
}

export interface PerSetDiffCardOnlyInJson {
  id: string
  set_id: string
  number: string
  name: string | null
  rarity: string | null
  art_type: string | null
  sub_art_type: string | null
  image_url: string | null
  is_manga: number | null
  manually_edited_fields: string | null
  last_ct_sync_at: string | null
  last_ct_blueprint_id: number | null
}

export interface PerSetDiffCardOnlyInCt {
  id: number
  blueprint_id: number
  card_id: string
  expansion_id: number | null
  blueprint_name: string | null
  blueprint_number: string | null
  collector_number: string | null
  image_url: string | null
  onepiece_rarity: string | null
  language: string | null
  expansion_name: string | null
}

export interface PerSetDiffFieldEntry {
  json: any
  ct: any
  /** When true the field is on the card's manually_edited_fields mask and
   * will NOT be written by /apply. */
  protected?: boolean
}

export interface PerSetDiffMismatchedCard {
  id: string
  fields: Record<string, PerSetDiffFieldEntry>
}

export interface PerSetDiffSealedOnlyInDb {
  id: string
  name: string
  blueprint_id: number
}

export interface PerSetDiffSealedOnlyInCt {
  blueprint_id: number
  blueprint_name: string | null
}

export interface PerSetDiffSealedMismatched {
  id: string
  fields: Record<string, { json: any; ct: any }>
}

export interface PerSetDiffResponse {
  set: PerSetDiffSetSummary | null
  cards: {
    onlyInJson: PerSetDiffCardOnlyInJson[]
    onlyInCt: PerSetDiffCardOnlyInCt[]
    mismatched: PerSetDiffMismatchedCard[]
  }
  sealed: {
    onlyInDb: PerSetDiffSealedOnlyInDb[]
    onlyInCt: PerSetDiffSealedOnlyInCt[]
    mismatched: PerSetDiffSealedMismatched[]
  }
}

/** POST /sync/by-set/:setId/apply request body. */
export interface PerSetSyncApplyBody {
  apply_cards?: boolean
  apply_sealed?: boolean
  mark_as_cardtrader?: boolean
}

/** POST /sync/by-set/:setId/apply response envelope. */
export interface PerSetSyncApplyResponse {
  ok: true
  batchId: string
  set_id: string
  totals: {
    cards_updated: number
    sealed_updated: number
  }
  cards_written: Array<{ card_id: string; written: string[] }>
}

/** GET /parity/:setId response envelope. */
export interface PerSetParityResponse {
  set_id: string
  json_cards: number
  ct_cards: number
  mismatched_cards: number
  manual_protected: number
  json_sealed: number
  ct_sealed: number
  recommendation: ParityRecommendation
}

/** GET /sync/by-set/:setId/history response envelope. */
export interface PerSetSyncHistoryEntry {
  audit_id: number
  user_id: number | null
  card_id: string | null
  action: string
  before: any
  after: any
  metadata: any
  created_at: string
}

export interface PerSetSyncHistoryResponse {
  set_id: string
  entries: PerSetSyncHistoryEntry[]
}

/** POST /sealed/sync/:setId response envelope. */
export interface SealedSyncForSetResponse {
  ok: true
  set_id: string
  imported: number
  skipped: number
  errors: string[]
  sealed_in_set: number
}

/** POST /sets/:setId/retire-json response envelope (success). */
export interface RetireJsonResponse {
  ok: true
  set_id: string
  seed_source: 'cardtrader'
  forced: boolean
}

/** POST /sets/:setId/retire-json response envelope (409 parity_not_green). */
export interface RetireJsonError {
  error: 'parity_not_green'
  message: string
  details: { json_cards: number; ct_cards: number; realMismatches: number }
}

/**
 * Helper: parse a card row's manually_edited_fields JSON into a Set.
 * Returns an empty set on null/invalid input.
 */
export function parseManuallyEditedFields(json: string | null | undefined): Set<string> {
  if (!json) return new Set()
  try {
    const arr = JSON.parse(json)
    if (Array.isArray(arr)) return new Set(arr.filter((x): x is string => typeof x === 'string'))
  } catch {
    /* ignore */
  }
  return new Set()
}
